import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
    Card,
    FormControl,
    Grid,
    IconButton,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useFormikContext } from "formik";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { McpServerDto } from "src/types/mcpServer";

interface McpServerRowProps {
    server: any;
    index: number;
    availableServers: McpServerDto[];
    onDelete: () => void;
    assignedServerIds: string[];
}

const McpServerRow: FC<McpServerRowProps> = ({
    server,
    index,
    availableServers,
    onDelete,
    assignedServerIds,
}) => {
    const { t } = useTranslation();
    const { handleChange, handleBlur } = useFormikContext<any>();

    const selectedId = server.mcpServerId ?? "";
    const selectedServer = availableServers.find(s => s.id === selectedId);
    const selectedUrl = selectedServer?.serverUrl ?? "";

    const isDisabled = (id: string) =>
        assignedServerIds.includes(id) && id !== selectedId;

    const handleServerChange = (e: any) => {
        handleChange(e);
    };

    return (
        <Card
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
        >
            <Grid container alignItems="stretch">
                {/* Server name dropdown */}
                <Grid item xs={5} sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                        {t("McpServer.Name")}
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            size="small"
                            value={selectedId}
                            displayEmpty
                            onChange={handleServerChange}
                            onBlur={handleBlur}
                            name={`mcpServers.${index}.mcpServerId`}
                            sx={{ height: 40 }}
                            renderValue={(value) => {
                                if (!value) {
                                    return t("McpServer.SelectServer");
                                }
                                
                                const found = availableServers.find(srv => srv.id === value);
                                
                                return found?.name ?? "";
                            }}
                        >
                            <MenuItem value="" disabled>
                                <ListItemText primary={t("McpServer.SelectServer")} />
                            </MenuItem>
                            {availableServers.map(s => (
                                <MenuItem
                                    key={s.id}
                                    value={s.id}
                                    disabled={isDisabled(s.id)}
                                >
                                    <ListItemText primary={s.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Server URL — read-only single-line input */}
                <Grid item xs={6} sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                        {t("McpServer.ServerUrl")}
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        value={selectedUrl}
                        variant="outlined"
                        disabled
                        placeholder={!selectedId ? t("McpServer.ServerUrlAutoFill") : ""}
                        inputProps={{ style: { fontFamily: "monospace", fontSize: "0.8rem" } }}
                        sx={{ '& .MuiInputBase-root': { height: 40 } }}
                    />
                </Grid>

                {/* Delete */}
                <Grid item xs={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconButton
                        onClick={onDelete}
                        size="small"
                        sx={{ color: "text.disabled", borderRadius: 1, marginTop: "20px" }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Grid>
            </Grid>
        </Card>
    );
};

export default McpServerRow;
